import React, { useState, useEffect } from 'react';
import {
  FileText, Download, Filter, Search, User, Calendar, BarChart3, Clock,
  AlertTriangle, ChevronDown, ChevronUp, Mail, Plus, Trash2, Play, Pause,
  Timer, Loader2, RefreshCw, X, Check, Eye, Building2, TrendingUp, CalendarDays
} from 'lucide-react';
import {
  reportsService, scheduledTasksService,
  AttendanceReport, OvertimeReport, LateReport, DepartmentSummaryReport, ReportStats,
  ScheduledTask, ScheduledTaskCreateDto,
  REPORT_TYPES, FREQUENCY_OPTIONS, FORMAT_OPTIONS,
  departmentService, employeeService
} from '../services';
import { useAppContext } from '../context/AppContext';
import { Role } from '../types';
import authService from '../services/authService';
import SiteLocationFilter from '../components/SiteLocationFilter';
import { useTranslation } from 'react-i18next';
import { uyar, hata, onayla } from '../components/GlobalModal';

type TabType = 'attendance' | 'overtime' | 'late' | 'onleave' | 'devicesync' | 'department' | 'scheduled';

const Reports = () => {
  const { t } = useTranslation();
  const { currentUser } = useAppContext();
  const isDeptManager = currentUser.role === Role.DEPARTMENT_MANAGER;
  const storedUser = authService.getStoredUser();
  const deptManagerDeptId = isDeptManager ? storedUser?.departmentID : null;

  // State
  const [activeTab, setActiveTab] = useState<TabType>('attendance');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ReportStats | null>(null);

  // Report data
  const [attendanceReport, setAttendanceReport] = useState<AttendanceReport | null>(null);
  const [overtimeReport, setOvertimeReport] = useState<OvertimeReport | null>(null);
  const [lateReport, setLateReport] = useState<LateReport | null>(null);
  const [departmentReport, setDepartmentReport] = useState<DepartmentSummaryReport | null>(null);

  // Scheduled tasks
  const [scheduledTasks, setScheduledTasks] = useState<ScheduledTask[]>([]);
  // 2026-08-14 (kullanici talebi): Izinliler raporu — diger raporlarla ayni duzende
  const [onLeaveReport, setOnLeaveReport] = useState<any>(null);
  // 2026-08-14: Cihaz senkron raporu — Ayarlar ekrani buraya yonlendiriyordu
  const [syncReport, setSyncReport] = useState<any>(null);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [departmentId, setDepartmentId] = useState<number | null>(deptManagerDeptId);
  const [siteId, setSiteId] = useState<string>('all');
  const [locationId, setLocationId] = useState<string>('all');
  const [departments, setDepartments] = useState<{ id: number; name: string }[]>([]);
  const [employees, setEmployees] = useState<{ id: number; firstName: string; lastName: string; email?: string }[]>([]);

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState<ScheduledTask | null>(null);
  const [taskForm, setTaskForm] = useState<ScheduledTaskCreateDto>({
    title: '',
    reportType: 'ATTENDANCE',
    frequency: 'WEEKLY',
    runTime: '08:00',
    format: 'EXCEL',
    recipientEmails: []
  });
  const [emailInput, setEmailInput] = useState('');
  const [saving, setSaving] = useState(false);

  // Preview Modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load report when tab or filters change
  useEffect(() => {
    if (activeTab !== 'scheduled') {
      loadReport();
    }
  }, [activeTab, startDate, endDate, departmentId]);

  const loadInitialData = async () => {
    try {
      const [statsRes, deptRes, empRes] = await Promise.all([
        reportsService.getStats(),
        departmentService.getAll(),
        employeeService.getAll()
      ]);

      if (statsRes.success && statsRes.data) {
        setStats(statsRes.data);
      }
      if (deptRes.success && deptRes.data) {
        setDepartments(deptRes.data.map((d: any) => ({ id: d.id || d.departmentId, name: d.name || d.departmentName })));
      }
      if (empRes.success && empRes.data) {
        setEmployees(empRes.data.map((e: any) => ({
          id: e.id || e.employeeId,
          firstName: e.firstName,
          lastName: e.lastName,
          email: e.email
        })));
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    const filters: any = {
      startDate,
      endDate,
      departmentId: departmentId || undefined,
      siteId: siteId !== 'all' ? Number(siteId) : undefined,
      locationId: locationId !== 'all' ? Number(locationId) : undefined,
    };

    try {
      switch (activeTab) {
        case 'attendance':
          const attRes = await reportsService.getAttendanceReport(filters);
          if (attRes.success && attRes.data) {
            setAttendanceReport(attRes.data);
          }
          break;
        case 'overtime':
          const otRes = await reportsService.getOvertimeReport(filters);
          if (otRes.success && otRes.data) {
            setOvertimeReport(otRes.data);
          }
          break;
        case 'late':
          const lateRes = await reportsService.getLateReport(filters);
          if (lateRes.success && lateRes.data) {
            setLateReport(lateRes.data);
          }
          break;
        case 'devicesync': {
          const params = new URLSearchParams({ startDate, endDate });
          const res = await fetch(`/api/reports/device-sync?${params}`, { credentials: 'include' });
          const json = await res.json();
          if (json?.success && json.data) setSyncReport(json.data);
          break;
        }
        case 'onleave': {
          const params = new URLSearchParams({ startDate, endDate });
          if (departmentId) params.append('departmentId', String(departmentId));
          if (siteId !== 'all') params.append('siteId', String(siteId));
          if (locationId !== 'all') params.append('locationId', String(locationId));
          const res = await fetch(`/api/reports/on-leave?${params}`, { credentials: 'include' });
          const json = await res.json();
          if (json?.success && json.data) setOnLeaveReport(json.data);
          break;
        }
        case 'department':
          const deptRes = await reportsService.getDepartmentSummary(filters);
          if (deptRes.success && deptRes.data) {
            setDepartmentReport(deptRes.data);
          }
          break;
      }
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadScheduledTasks = async () => {
    setLoadingTasks(true);
    try {
      const res = await scheduledTasksService.getAll();
      if (res.success && res.data) {
        setScheduledTasks(res.data);
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'scheduled') {
      loadScheduledTasks();
    }
  }, [activeTab]);

  const handleSaveTask = async () => {
    if (!taskForm.title || !taskForm.recipientEmails?.length) {
      uyar(t('reports.validationTitleEmail'));
      return;
    }

    setSaving(true);
    try {
      if (editingTask) {
        await scheduledTasksService.update(editingTask.id, taskForm);
      } else {
        await scheduledTasksService.create(taskForm);
      }
      setShowModal(false);
      setEditingTask(null);
      setTaskForm({
        title: '',
        reportType: 'ATTENDANCE',
        frequency: 'WEEKLY',
        runTime: '08:00',
        format: 'EXCEL',
        recipientEmails: []
      });
      setEmailInput('');
      loadScheduledTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      uyar(t('reports.errorSavingTask'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (email && email.includes('@') && !taskForm.recipientEmails?.includes(email)) {
      setTaskForm({
        ...taskForm,
        recipientEmails: [...(taskForm.recipientEmails || []), email]
      });
      setEmailInput('');
    }
  };

  const handleRemoveEmail = (email: string) => {
    setTaskForm({
      ...taskForm,
      recipientEmails: taskForm.recipientEmails?.filter(e => e !== email) || []
    });
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await scheduledTasksService.toggleStatus(id);
      loadScheduledTasks();
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const handleDeleteTask = async (id: number) => {
    if (!(await onayla(t('reports.confirmDeleteTask')))) return;
    try {
      await scheduledTasksService.delete(id);
      loadScheduledTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handlePreview = async (id: number) => {
    setPreviewLoading(true);
    setShowPreviewModal(true);
    try {
      const response = await fetch(`/api/scheduled-tasks/${id}/run-now?preview=true`, {
        method: 'POST'
      });
      const result = await response.json();
      if (result.success) {
        setPreviewData(result.data);
      } else {
        uyar(result.message || t('reports.previewFailed'));
        setShowPreviewModal(false);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      uyar(t('reports.previewError'));
      setShowPreviewModal(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!previewData?.id) return;
    setSendingEmail(true);
    try {
      const response = await fetch(`/api/scheduled-tasks/${previewData.id}/run-now`, {
        method: 'POST'
      });
      const result = await response.json();
      if (result.success) {
        setPreviewData(result.data);
        if (result.data.emailSent) {
          uyar(t('reports.emailSentSuccess'));
        } else {
          uyar(t('reports.emailSentFailSmtp'));
        }
        loadScheduledTasks();
      } else {
        uyar(result.message || t('reports.emailSendFailed'));
      }
    } catch (error) {
      console.error('Error sending email:', error);
      uyar(t('reports.emailSendError'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleRunNow = async (id: number) => {
    // Open preview modal directly
    handlePreview(id);
  };

  const toggleRow = (id: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const formatDate = (date: string) => new Date(date).toLocaleDateString();

  const API_BASE = import.meta.env.VITE_API_URL || '';

  const exportToExcel = () => {
    const params = new URLSearchParams({
      startDate,
      endDate
    });
    if (departmentId) params.append('departmentId', departmentId.toString());
    if (siteId !== 'all') params.append('siteId', siteId);
    if (locationId !== 'all') params.append('locationId', locationId);

    let endpoint = '';
    switch (activeTab) {
      case 'attendance':
        endpoint = 'attendance';
        break;
      case 'overtime':
        endpoint = 'overtime';
        break;
      case 'late':
        endpoint = 'late';
        break;
      case 'onleave':
        endpoint = 'on-leave';
        break;
      default:
        return;
    }

    window.location.href = `${API_BASE}/api/reports/${endpoint}/export/excel?${params}`;
  };

  const tabs = [
    { id: 'attendance', label: t('reports.attendance'), icon: <User size={16} /> },
    { id: 'overtime', label: t('reports.overtime'), icon: <Clock size={16} /> },
    { id: 'late', label: t('reports.late'), icon: <AlertTriangle size={16} /> },
    { id: 'onleave', label: 'İZİNLİLER', icon: <CalendarDays size={16} /> },
    { id: 'devicesync', label: 'CİHAZ SENKRON', icon: <RefreshCw size={16} /> },
    { id: 'department', label: t('reports.department'), icon: <Building2 size={16} /> },
    { id: 'scheduled', label: t('reports.scheduled'), icon: <Timer size={16} /> },
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">{t('reports.title')}</h1>
          <p className="text-slate-500 text-sm font-medium">{t('reports.subtitle')}</p>
        </div>
        <div className="flex gap-3 w-full lg:w-auto">
          {activeTab === 'scheduled' ? (
            <button
              onClick={() => {
                setEditingTask(null);
                setTaskForm({
                  title: '',
                  reportType: 'ATTENDANCE',
                  frequency: 'WEEKLY',
                  runTime: '08:00',
                  format: 'EXCEL',
                  recipientEmails: []
                });
                setEmailInput('');
                setShowModal(true);
              }}
              className="flex-1 lg:flex-none bg-indigo-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all active:scale-95"
            >
              <Plus size={18} /> {t('reports.newTask')}
            </button>
          ) : (
            <>
              <button
                onClick={loadReport}
                className="flex-1 lg:flex-none bg-white border border-slate-200 text-slate-700 px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-slate-50 transition-all shadow-sm"
              >
                <RefreshCw size={18} className="text-slate-500" /> {t('reports.refresh')}
              </button>
              {activeTab !== 'department' && (
                <button
                  onClick={exportToExcel}
                  className="flex-1 lg:flex-none bg-green-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-green-700 shadow-xl shadow-green-100 transition-all active:scale-95"
                >
                  <Download size={18} /> Excel
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-slate-900 rounded-[2rem] p-6 text-white relative overflow-hidden shadow-xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500 opacity-20 rounded-full -mr-16 -mt-16 blur-3xl"></div>
            <div className="relative z-10 flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-blue-400">
                <User size={24} />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('reports.presentToday')}</p>
                <h3 className="text-2xl font-black">{stats.today.presentCount} / {stats.today.totalEmployees}</h3>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-[2rem] p-6 border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center">
              <AlertTriangle size={24} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('reports.lateToday')}</p>
              <h3 className="text-2xl font-black text-slate-800">{stats.today.lateCount}</h3>
            </div>
          </div>
          <div className="bg-white rounded-[2rem] p-6 border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center">
              <Clock size={24} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('reports.overtimeThisMonth')}</p>
              <h3 className="text-2xl font-black text-slate-800">{stats.thisMonth.totalOvertimeHours} {t('reports.hours')}</h3>
            </div>
          </div>
          <div className="bg-white rounded-[2rem] p-6 border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center">
              <TrendingUp size={24} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('reports.workThisMonth')}</p>
              <h3 className="text-2xl font-black text-slate-800">{stats.thisMonth.totalWorkHours} {t('reports.hours')}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Tabs and Content */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
        {/* Tab Headers */}
        <div className="px-8 pt-8 flex gap-8 border-b border-slate-50 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`pb-5 text-xs font-black uppercase tracking-widest transition-all border-b-2 flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-blue-600 border-blue-600'
                  : 'text-slate-400 border-transparent hover:text-slate-600'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        {activeTab !== 'scheduled' && (
          <div className="px-8 py-4 bg-slate-50/50 border-b border-slate-100 flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-slate-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
              />
              <span className="text-slate-400">-</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <select
              value={departmentId || ''}
              onChange={(e) => !isDeptManager && setDepartmentId(e.target.value ? Number(e.target.value) : null)}
              disabled={isDeptManager}
              className={`bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm ${isDeptManager ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {!isDeptManager && <option value="">{t('reports.allDepartments')}</option>}
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            {/* Ag 2026: Şantiye + Lokasyon filtresi (backend zaten destekliyor) */}
            <SiteLocationFilter
              selectedSiteId={siteId}
              selectedLocationId={locationId}
              onSiteChange={setSiteId}
              onLocationChange={setLocationId}
            />
          </div>
        )}

        {/* Content */}
        <div className="p-8">
          {loading && activeTab !== 'scheduled' ? (
            <div className="py-20 text-center">
              <Loader2 size={48} className="mx-auto text-blue-500 animate-spin mb-4" />
              <p className="text-slate-400 font-bold">{t('common.loading')}</p>
            </div>
          ) : (
            <>
              {/* Attendance Report */}
              {activeTab === 'attendance' && attendanceReport && (
                <div className="space-y-4">
                  <div className="text-sm text-slate-500 mb-4">
                    {formatDate(attendanceReport.startDate)} - {formatDate(attendanceReport.endDate)} | {t('reports.totalWorkDays', { count: attendanceReport.totalWorkDays })}
                  </div>
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                        <th className="pb-4 px-2">{t('reports.employee')}</th>
                        <th className="pb-4 px-2 text-center">{t('reports.workDays')}</th>
                        <th className="pb-4 px-2 text-center">{t('reports.absent')}</th>
                        <th className="pb-4 px-2 text-center">{t('reports.lateness')}</th>
                        <th className="pb-4 px-2 text-center">{t('reports.overtimeShort')}</th>
                        <th className="pb-4 px-2 text-right">{t('reports.attendanceRate')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {attendanceReport.employees.map(emp => (
                        <tr key={emp.employeeId} className="hover:bg-slate-50/50">
                          <td className="py-4 px-2">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center font-black text-xs">
                                {emp.firstName[0]}{emp.lastName[0]}
                              </div>
                              <div>
                                <div className="text-sm font-bold text-slate-800">{emp.fullName}</div>
                                <div className="text-[10px] text-slate-400">{emp.departmentName}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-2 text-center font-bold">{emp.workDays}</td>
                          <td className="py-4 px-2 text-center">
                            <span className={`font-bold ${emp.absentDays > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                              {emp.absentDays}
                            </span>
                          </td>
                          <td className="py-4 px-2 text-center">
                            <span className={`font-bold ${emp.lateDays > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                              {emp.lateDays} ({emp.lateHours}{t('reports.hoursAbbr')})
                            </span>
                          </td>
                          <td className="py-4 px-2 text-center">
                            <span className={`font-bold ${emp.overtimeHours > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                              {emp.overtimeHours}{t('reports.hoursAbbr')}
                            </span>
                          </td>
                          <td className="py-4 px-2 text-right">
                            <span className={`px-2 py-1 rounded-lg text-xs font-bold ${
                              emp.attendanceRate >= 90 ? 'bg-green-50 text-green-600' :
                              emp.attendanceRate >= 70 ? 'bg-amber-50 text-amber-600' :
                              'bg-red-50 text-red-600'
                            }`}>
                              %{emp.attendanceRate}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Overtime Report */}
              {activeTab === 'overtime' && overtimeReport && (
                <div className="space-y-4">
                  <div className="text-sm text-slate-500 mb-4">
                    {t('reports.totalOvertimeHours', { hours: overtimeReport.totalOvertimeHours })}
                  </div>
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                        <th className="pb-4 px-2">{t('reports.employee')}</th>
                        <th className="pb-4 px-2">{t('reports.department')}</th>
                        <th className="pb-4 px-2 text-center">{t('reports.workDays')}</th>
                        <th className="pb-4 px-2 text-right">{t('reports.overtimeHoursCol')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {overtimeReport.employees.map(emp => (
                        <tr key={emp.employeeId} className="hover:bg-slate-50/50">
                          <td className="py-4 px-2">
                            <div className="font-bold text-slate-800">{emp.fullName}</div>
                          </td>
                          <td className="py-4 px-2 text-slate-600">{emp.departmentName}</td>
                          <td className="py-4 px-2 text-center">{emp.workDays}</td>
                          <td className="py-4 px-2 text-right">
                            <span className="font-black text-green-600">{emp.overtimeHours}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Late Report */}
              {activeTab === 'late' && lateReport && (
                <div className="space-y-4">
                  <div className="text-sm text-slate-500 mb-4">
                    {t('reports.totalLateSummary', { days: lateReport.totalLateDays, hours: lateReport.totalLateHours })}
                  </div>
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                        <th className="pb-4 px-2">{t('reports.employee')}</th>
                        <th className="pb-4 px-2">{t('reports.department')}</th>
                        <th className="pb-4 px-2 text-center">{t('reports.lateDays')}</th>
                        <th className="pb-4 px-2 text-center">{t('reports.totalMinutes')}</th>
                        <th className="pb-4 px-2 text-right">{t('reports.avgMinutes')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {lateReport.employees.map(emp => (
                        <tr key={emp.employeeId} className="hover:bg-slate-50/50">
                          <td className="py-4 px-2">
                            <div className="font-bold text-slate-800">{emp.fullName}</div>
                          </td>
                          <td className="py-4 px-2 text-slate-600">{emp.departmentName}</td>
                          <td className="py-4 px-2 text-center font-bold text-amber-600">{emp.lateDays}</td>
                          <td className="py-4 px-2 text-center">{emp.totalLateMinutes}</td>
                          <td className="py-4 px-2 text-right">{emp.avgLateMinutes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Department Summary */}
              {/* 2026-08-14 (kullanici talebi): CIHAZ SENKRON RAPORU.
                  Ayarlar > Cihaz Senkron ekrani "detaylar Raporlar sayfasindan
                  goruntulenebilir" diyordu ama boyle bir rapor yoktu. */}
              {activeTab === 'devicesync' && syncReport && (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-slate-50 rounded-2xl p-4">
                      <div className="text-[11px] text-slate-500 font-black uppercase tracking-widest">Son Çalışma</div>
                      <div className="text-lg font-black text-slate-800 mt-1">
                        {syncReport.lastRun
                          ? new Date(syncReport.lastRun).toLocaleString('tr-TR')
                          : 'Hiç çalışmadı'}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-2xl p-4">
                      <div className="text-[11px] text-green-600 font-black uppercase tracking-widest">Başarılı</div>
                      <div className="text-2xl font-black text-green-700 mt-1">{syncReport.successCount ?? 0}</div>
                    </div>
                    <div className="bg-amber-50 rounded-2xl p-4">
                      <div className="text-[11px] text-amber-600 font-black uppercase tracking-widest">Kısmi / Hatalı</div>
                      <div className="text-2xl font-black text-amber-700 mt-1">
                        {(syncReport.partialCount ?? 0) + (syncReport.failedCount ?? 0)}
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-2xl p-4">
                      <div className="text-[11px] text-blue-600 font-black uppercase tracking-widest">Çekilen Kayıt</div>
                      <div className="text-2xl font-black text-blue-700 mt-1">{syncReport.totalRecords ?? 0}</div>
                    </div>
                  </div>

                  {(!syncReport.runs || syncReport.runs.length === 0) ? (
                    <div className="text-center py-16 text-slate-400">
                      <RefreshCw size={40} className="mx-auto mb-3 opacity-40" />
                      <p className="font-medium">Bu tarih aralığında senkronizasyon kaydı yok</p>
                      <p className="text-xs mt-2">
                        Otomatik senkron Ayarlar &gt; Cihaz Senkron sekmesinden açılır.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-[11px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">
                            <th className="pb-3">Başlangıç</th>
                            <th className="pb-3 text-center">Tetik</th>
                            <th className="pb-3 text-center">Durum</th>
                            <th className="pb-3 text-center">Cihaz</th>
                            <th className="pb-3 text-center">Başarılı</th>
                            <th className="pb-3 text-center">Hatalı</th>
                            <th className="pb-3 text-center">Kayıt</th>
                            <th className="pb-3 text-center">Süre</th>
                            <th className="pb-3">Not</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {syncReport.runs.map((k: any) => {
                            const durum = k.Status ?? k.status;
                            const tetik = k.TriggerType ?? k.triggerType;
                            return (
                              <tr key={k.Id ?? k.id} className="hover:bg-slate-50/60">
                                <td className="py-4 text-sm text-slate-700">
                                  {new Date(k.StartedAt ?? k.startedAt).toLocaleString('tr-TR')}
                                </td>
                                <td className="py-4 text-center">
                                  <span className={`px-2 py-1 rounded-md text-[11px] font-bold ${
                                    tetik === 'MANUAL' ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-600'
                                  }`}>
                                    {tetik === 'MANUAL' ? 'Manuel' : 'Otomatik'}
                                  </span>
                                </td>
                                <td className="py-4 text-center">
                                  <span className={`px-2 py-1 rounded-full text-[11px] font-bold ${
                                    durum === 'SUCCESS' ? 'bg-green-50 text-green-700'
                                      : durum === 'PARTIAL' ? 'bg-amber-50 text-amber-700'
                                      : 'bg-red-50 text-red-700'
                                  }`}>
                                    {durum === 'SUCCESS' ? 'Başarılı' : durum === 'PARTIAL' ? 'Kısmi' : 'Başarısız'}
                                  </span>
                                </td>
                                <td className="py-4 text-center font-bold text-slate-800">{k.TotalDevices ?? k.totalDevices ?? 0}</td>
                                <td className="py-4 text-center text-green-600 font-bold">{k.SuccessCount ?? k.successCount ?? 0}</td>
                                <td className="py-4 text-center text-red-600 font-bold">{k.FailedCount ?? k.failedCount ?? 0}</td>
                                <td className="py-4 text-center font-black text-blue-700">{k.TotalRecords ?? k.totalRecords ?? 0}</td>
                                <td className="py-4 text-center text-sm text-slate-500">
                                  {(k.DurationSec ?? k.durationSec) != null ? `${k.DurationSec ?? k.durationSec} sn` : '-'}
                                </td>
                                <td className="py-4 text-sm text-slate-500 max-w-xs truncate">
                                  {k.Detail ?? k.detail ?? '-'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* 2026-08-14 (kullanici talebi): IZINLILER RAPORU */}
              {activeTab === 'onleave' && onLeaveReport && (
                <div>
                  <div className="text-sm text-slate-500 mb-4">
                    {new Date(onLeaveReport.startDate).toLocaleDateString('tr-TR')} - {new Date(onLeaveReport.endDate).toLocaleDateString('tr-TR')}
                    {' | '}Toplam <strong className="text-slate-700">{onLeaveReport.total ?? 0}</strong> izin kaydı
                  </div>

                  {(!onLeaveReport.leaves || onLeaveReport.leaves.length === 0) ? (
                    <div className="text-center py-16 text-slate-400">
                      <CalendarDays size={40} className="mx-auto mb-3 opacity-40" />
                      <p className="font-medium">Bu tarih aralığında izin kaydı yok</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-[11px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">
                            <th className="pb-3">Personel</th>
                            <th className="pb-3">İzin Türü</th>
                            <th className="pb-3 text-center">Başlangıç</th>
                            <th className="pb-3 text-center">Bitiş</th>
                            <th className="pb-3 text-center">Süre</th>
                            <th className="pb-3 text-center">Durum</th>
                            <th className="pb-3">Açıklama</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {onLeaveReport.leaves.map((iz: any) => (
                            <tr key={iz.leaveId} className="hover:bg-slate-50/60">
                              <td className="py-4">
                                <div className="font-bold text-slate-800">{iz.fullName}</div>
                                <div className="text-xs text-slate-400">
                                  {iz.employeeCode || '-'}{iz.departmentName ? ` · ${iz.departmentName}` : ''}
                                </div>
                              </td>
                              <td className="py-4">
                                <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-xs font-bold">
                                  {iz.leaveType}
                                </span>
                              </td>
                              <td className="py-4 text-center text-sm text-slate-600">
                                {new Date(iz.startDate).toLocaleDateString('tr-TR')}
                              </td>
                              <td className="py-4 text-center text-sm text-slate-600">
                                {new Date(iz.endDate).toLocaleDateString('tr-TR')}
                              </td>
                              <td className="py-4 text-center">
                                {iz.isHourly ? (
                                  <span className="font-bold text-amber-600 text-sm">
                                    {iz.timeRange || `${Math.floor((iz.hourlyMinutes || 0) / 60)}sa ${(iz.hourlyMinutes || 0) % 60}dk`}
                                  </span>
                                ) : (
                                  <span className="font-bold text-slate-800">{iz.daysCount} gün</span>
                                )}
                              </td>
                              <td className="py-4 text-center">
                                <span className={`px-2 py-1 rounded-full text-[11px] font-bold ${
                                  iz.status === 'Approved' ? 'bg-green-50 text-green-700'
                                    : iz.status === 'Rejected' ? 'bg-red-50 text-red-700'
                                    : 'bg-amber-50 text-amber-700'
                                }`}>
                                  {iz.status === 'Approved' ? 'Onaylandı'
                                    : iz.status === 'Rejected' ? 'Reddedildi'
                                    : iz.status === 'Cancelled' ? 'İptal' : 'Bekliyor'}
                                </span>
                              </td>
                              <td className="py-4 text-sm text-slate-500 max-w-xs truncate">{iz.reason || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'department' && departmentReport && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {departmentReport.departments.map(dept => (
                    <div key={dept.departmentID} className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                      <h3 className="font-black text-slate-800 text-lg mb-4">{dept.departmentName}</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-slate-500 text-sm">{t('reports.employees')}</span>
                          <span className="font-bold">{dept.employeeCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500 text-sm">{t('reports.workHours')}</span>
                          <span className="font-bold text-green-600">{dept.totalWorkHours ?? 0} {t('reports.hours')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500 text-sm">{t('reports.overtimeShort')}</span>
                          <span className="font-bold text-blue-600">{dept.overtimeHours ?? 0} {t('reports.hours')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500 text-sm">{t('reports.lateness')}</span>
                          <span className="font-bold text-amber-600">{dept.lateHours ?? 0} {t('reports.hours')}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Scheduled Tasks */}
              {activeTab === 'scheduled' && (
                <div className="space-y-6">
                  <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-6 flex items-start gap-4">
                    <Mail className="text-indigo-600 shrink-0" size={28} />
                    <div>
                      <h3 className="font-black text-indigo-900">{t('reports.autoEmailReports')}</h3>
                      <p className="text-sm text-indigo-700 mt-1">
                        {t('reports.autoEmailReportsDesc')}
                      </p>
                    </div>
                  </div>

                  {loadingTasks ? (
                    <div className="py-20 text-center">
                      <Loader2 size={48} className="mx-auto text-indigo-500 animate-spin mb-4" />
                    </div>
                  ) : scheduledTasks.length === 0 ? (
                    <div className="py-20 text-center border-2 border-dashed border-slate-100 rounded-2xl">
                      <Clock size={48} className="mx-auto text-slate-200 mb-4" />
                      <p className="text-slate-400 font-bold uppercase text-xs">{t('reports.noTasksYet')}</p>
                      <button
                        onClick={() => setShowModal(true)}
                        className="mt-4 text-indigo-600 font-bold text-sm hover:underline"
                      >
                        {t('reports.createFirstTask')}
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {scheduledTasks.map(task => (
                        <div key={task.id} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all">
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex gap-4">
                              <div className={`p-3 rounded-xl ${task.status === 'ACTIVE' ? 'bg-green-50 text-green-600' : 'bg-slate-50 text-slate-400'}`}>
                                <Timer size={24} />
                              </div>
                              <div>
                                <h4 className="font-black text-slate-800">{task.title}</h4>
                                <div className="flex gap-2 mt-1">
                                  <span className="text-[10px] font-bold uppercase text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                    {REPORT_TYPES.find(r => r.value === task.reportType)?.label || task.reportType}
                                  </span>
                                  <span className="text-[10px] font-bold uppercase text-slate-500 bg-slate-50 px-2 py-0.5 rounded">
                                    {FREQUENCY_OPTIONS.find(f => f.value === task.frequency)?.label || task.frequency}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleToggleStatus(task.id)}
                                className={`p-2 rounded-lg ${task.status === 'ACTIVE' ? 'text-amber-500 hover:bg-amber-50' : 'text-green-500 hover:bg-green-50'}`}
                                title={task.status === 'ACTIVE' ? t('reports.pause') : t('reports.start')}
                              >
                                {task.status === 'ACTIVE' ? <Pause size={18} /> : <Play size={18} />}
                              </button>
                              <button
                                onClick={() => handleRunNow(task.id)}
                                className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                title={t('reports.runNow')}
                              >
                                <Play size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteTask(task.id)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>

                          <div className="space-y-2 pt-4 border-t border-slate-100">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500">{t('reports.nextRun')}</span>
                              <span className="font-bold">{task.nextRun ? formatDate(task.nextRun) : '-'}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500">{t('reports.format')}</span>
                              <span className="font-bold">{task.format}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500">{t('reports.recipients')}</span>
                              <span className="font-bold text-right truncate max-w-[150px]" title={task.recipients.map(r => r.email).join(', ')}>
                                {task.recipients.length > 0 ? task.recipients.map(r => r.email).join(', ') : '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Task Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-black text-slate-800">
                  {editingTask ? t('reports.editTask') : t('reports.newTask')}
                </h2>
                <p className="text-sm text-slate-500">{t('reports.autoReportSending')}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-xl">
                <X size={24} className="text-slate-400" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">{t('reports.taskTitle')}</label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  placeholder={t('reports.taskTitlePlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('reports.reportType')}</label>
                  <select
                    value={taskForm.reportType}
                    onChange={(e) => setTaskForm({ ...taskForm, reportType: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    {REPORT_TYPES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('reports.frequency')}</label>
                  <select
                    value={taskForm.frequency}
                    onChange={(e) => setTaskForm({ ...taskForm, frequency: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    {FREQUENCY_OPTIONS.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('reports.runTime')}</label>
                  <input
                    type="time"
                    value={taskForm.runTime}
                    onChange={(e) => setTaskForm({ ...taskForm, runTime: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('reports.format')}</label>
                  <select
                    value={taskForm.format}
                    onChange={(e) => setTaskForm({ ...taskForm, format: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    {FORMAT_OPTIONS.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-2">{t('reports.emailAddresses')}</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddEmail();
                      }
                    }}
                    placeholder={t('reports.emailPlaceholder')}
                    className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleAddEmail}
                    className="px-4 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {taskForm.recipientEmails?.map((email, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium"
                    >
                      <Mail size={14} />
                      {email}
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        className="text-indigo-500 hover:text-indigo-700"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                  {(!taskForm.recipientEmails || taskForm.recipientEmails.length === 0) && (
                    <p className="text-slate-400 text-sm">{t('reports.noEmailsAdded')}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 border border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-white"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveTask}
                disabled={saving || !taskForm.title || !taskForm.recipientEmails?.length}
                className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
              <div>
                <h2 className="text-xl font-bold">
                  {previewLoading ? t('reports.preparingReport') : previewData?.taskTitle || t('reports.reportPreview')}
                </h2>
                {previewData && (
                  <p className="text-indigo-100 text-sm mt-1">{previewData.reportTypeName}</p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewData(null);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              {previewLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 size={48} className="animate-spin text-indigo-600 mb-4" />
                  <p className="text-slate-500">{t('reports.preparingReport')}</p>
                </div>
              ) : previewData ? (
                <div className="space-y-6">
                  {/* Summary Card */}
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-indigo-600 rounded-xl text-white">
                        <BarChart3 size={24} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-slate-800 text-lg">{previewData.summary}</h3>
                        <p className="text-slate-500 text-sm mt-1">
                          {t('reports.reportDate')}: {new Date(previewData.executed).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Recipients */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h4 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                      <Mail size={18} className="text-indigo-600" />
                      {t('reports.recipients')} ({previewData.recipientCount})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {previewData.recipientEmails?.map((email: string, i: number) => (
                        <span key={i} className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-600">
                          {email}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Data Table */}
                  {previewData.data && previewData.data.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                      <div className="p-4 bg-slate-50 border-b border-slate-200">
                        <h4 className="font-bold text-slate-700">{t('reports.reportData')} ({previewData.data.length} {t('reports.records')})</h4>
                      </div>
                      <div className="overflow-auto max-h-80">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 sticky top-0">
                            <tr>
                              {Object.keys(previewData.data[0] || {}).map((key) => (
                                <th key={key} className="px-4 py-3 text-left font-bold text-slate-600 whitespace-nowrap">
                                  {key === 'firstName' ? t('reports.firstName') :
                                   key === 'lastName' ? t('reports.lastName') :
                                   key === 'departmentName' ? t('reports.department') :
                                   key === 'workDays' ? t('reports.workDays') :
                                   key === 'lateDays' ? t('reports.lateDays') :
                                   key === 'totalLateMinutes' ? t('reports.totalLateMinutes') :
                                   key === 'totalOvertimeMinutes' ? t('reports.totalOvertimeMinutes') :
                                   key}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {previewData.data.slice(0, 50).map((row: any, i: number) => (
                              <tr key={i} className="hover:bg-slate-50">
                                {Object.values(row).map((val: any, j: number) => (
                                  <td key={j} className="px-4 py-3 text-slate-700 whitespace-nowrap">
                                    {val ?? '-'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {previewData.data.length > 50 && (
                          <div className="p-3 bg-slate-50 text-center text-sm text-slate-500">
                            {t('reports.andMoreRecords', { count: previewData.data.length - 50 })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Email Status */}
                  {previewData.emailSent !== undefined && !previewData.preview && (
                    <div className={`p-4 rounded-xl flex items-center gap-3 ${
                      previewData.emailSent
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-amber-50 border border-amber-200'
                    }`}>
                      {previewData.emailSent ? (
                        <>
                          <Check size={24} className="text-green-600" />
                          <div>
                            <p className="font-bold text-green-800">{t('reports.emailSent')}</p>
                            <p className="text-green-600 text-sm">{t('reports.emailSentToRecipients', { count: previewData.recipientCount })}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <AlertTriangle size={24} className="text-amber-600" />
                          <div>
                            <p className="font-bold text-amber-800">{t('reports.emailNotSent')}</p>
                            <p className="text-amber-600 text-sm">{t('reports.checkSmtpSettings')}</p>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Footer */}
            {previewData && previewData.preview && (
              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
                <button
                  onClick={() => {
                    setShowPreviewModal(false);
                    setPreviewData(null);
                  }}
                  className="flex-1 py-3 border border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-white"
                >
                  {t('common.close')}
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !previewData.recipientEmails?.length}
                  className="flex-1 py-3 bg-green-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-green-700 disabled:opacity-50"
                >
                  {sendingEmail ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Mail size={18} />
                  )}
                  {t('reports.sendEmail')} ({previewData.recipientCount} {t('reports.recipientsCount')})
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
